/**
 * OpenAI provider adapter for ludicrous-gateway
 * Translates between Anthropic API format and OpenAI API format
 */

import { BaseProvider } from "./base-provider.js";
import type {
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse } from "../types/messages.js";
import type { ProviderConfig, ApiKeyConfig } from "../types/config.js";
import type { TextContent } from "../types/messages.js";

/**
 * Available OpenAI models
 */
export const OPENAI_MODELS = [
  { value: "gpt-4o", label: "GPT-4o (Best quality)" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Fast, cheaper)" },
  { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "o1", label: "o1 (Reasoning)" },
  { value: "o1-mini", label: "o1 Mini (Reasoning, cheaper)" },
];

/**
 * Default model aliases mapping Claude models to OpenAI models
 */
export const DEFAULT_OPENAI_ALIASES: Record<string, string> = {
  "claude-opus-4-5-20251101": "gpt-4o",
  "claude-sonnet-4-20251114": "gpt-4o-mini",
  "claude-3-5-haiku-20241022": "gpt-4o-mini",
};

/**
 * OpenAI message format
 */
interface OpenAIMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

/**
 * OpenAI request format
 */
interface OpenAIRequest {
  model: string;
  messages: OpenAIMessage[];
  stream: boolean;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  stop?: string[];
}

/**
 * OpenAI non-streaming response format
 */
interface OpenAIResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: "assistant";
      content: string;
    };
    finish_reason: "stop" | "length" | "content_filter" | null;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * OpenAI streaming chunk format
 */
interface OpenAIStreamChunk {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    delta: {
      role?: "assistant";
      content?: string;
    };
    finish_reason: "stop" | "length" | "content_filter" | null;
  }>;
}

/**
 * OpenAI error response format
 */
interface OpenAIError {
  error: {
    message: string;
    type: string;
    param?: string;
    code?: string;
  };
}

/**
 * OpenAI API provider
 */
export class OpenAIProvider extends BaseProvider {
  private apiKeys: ApiKeyConfig[];
  private currentKeyIndex: number = 0;

  constructor(config: ProviderConfig) {
    super(config);
    this.apiKeys = config.keys ?? [];

    if (this.apiKeys.length === 0) {
      this.logger.warn("No API keys configured for OpenAI provider");
    }

    // Apply default aliases if none provided
    if (this.modelAliases.size === 0) {
      for (const [from, to] of Object.entries(DEFAULT_OPENAI_ALIASES)) {
        this.modelAliases.set(from, to);
      }
    }
  }

  /**
   * Get the next API key (round-robin)
   */
  private getNextApiKey(): string | null {
    if (this.apiKeys.length === 0) {
      return null;
    }

    const key = this.apiKeys[this.currentKeyIndex];
    this.currentKeyIndex = (this.currentKeyIndex + 1) % this.apiKeys.length;
    return key?.key ?? null;
  }

  protected getDefaultBaseUrl(): string {
    return "https://api.openai.com/v1";
  }

  /**
   * Build headers for OpenAI API request
   */
  protected override buildHeaders(
    _context: ProviderRequestContext
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const apiKey = this.getNextApiKey();
    if (apiKey) {
      headers["Authorization"] = `Bearer ${apiKey}`;
    }

    return headers;
  }

  /**
   * Convert Anthropic messages to OpenAI format
   */
  private convertToOpenAIFormat(
    request: MessagesRequest,
    resolvedModel: string
  ): OpenAIRequest {
    const messages: OpenAIMessage[] = [];

    // Add system prompt if present
    if (request.system) {
      const systemText =
        typeof request.system === "string"
          ? request.system
          : request.system
              .filter((b): b is TextContent => b.type === "text")
              .map((b) => b.text)
              .join("\n");

      messages.push({
        role: "system",
        content: systemText,
      });
    }

    // Convert messages
    for (const msg of request.messages) {
      let content: string;

      if (typeof msg.content === "string") {
        content = msg.content;
      } else {
        // Extract text content from blocks
        content = msg.content
          .filter((b): b is TextContent => b.type === "text")
          .map((b) => b.text)
          .join("\n");
      }

      messages.push({
        role: msg.role,
        content,
      });
    }

    const openAIRequest: OpenAIRequest = {
      model: resolvedModel,
      messages,
      stream: request.stream ?? false,
    };

    // Add optional parameters
    if (request.temperature !== undefined) {
      openAIRequest.temperature = request.temperature;
    }
    if (request.max_tokens !== undefined) {
      openAIRequest.max_tokens = request.max_tokens;
    }
    if (request.top_p !== undefined) {
      openAIRequest.top_p = request.top_p;
    }
    if (request.stop_sequences && request.stop_sequences.length > 0) {
      openAIRequest.stop = request.stop_sequences;
    }

    return openAIRequest;
  }

  /**
   * Convert OpenAI response to Anthropic format
   */
  private convertFromOpenAIFormat(
    response: OpenAIResponse,
    model: string
  ): MessagesResponse {
    const choice = response.choices[0];

    return {
      id: response.id,
      type: "message",
      role: "assistant",
      content: [
        {
          type: "text",
          text: choice?.message?.content ?? "",
        },
      ],
      model,
      stop_reason: this.mapFinishReason(choice?.finish_reason),
      stop_sequence: null,
      usage: {
        input_tokens: response.usage?.prompt_tokens ?? 0,
        output_tokens: response.usage?.completion_tokens ?? 0,
      },
    };
  }

  /**
   * Map OpenAI finish_reason to Anthropic stop_reason
   */
  private mapFinishReason(
    finishReason: string | null | undefined
  ): "end_turn" | "max_tokens" | "stop_sequence" | null {
    switch (finishReason) {
      case "stop":
        return "end_turn";
      case "length":
        return "max_tokens";
      case "content_filter":
        return "end_turn";
      default:
        return null;
    }
  }

  /**
   * Handle OpenAI API errors
   */
  private handleError(status: number, errorBody: OpenAIError | string): Error {
    let message: string;

    if (typeof errorBody === "object" && errorBody.error) {
      message = errorBody.error.message;
    } else if (typeof errorBody === "string") {
      message = errorBody;
    } else {
      message = "Unknown OpenAI API error";
    }

    switch (status) {
      case 401:
        return new Error(`OpenAI API error: Invalid API key - ${message}`);
      case 429:
        return new Error(`OpenAI API error: Rate limit exceeded - ${message}`);
      case 500:
      case 502:
      case 503:
      case 504:
        return new Error(`OpenAI API error: Server error (${status}) - ${message}`);
      default:
        return new Error(`OpenAI API error: ${status} - ${message}`);
    }
  }

  /**
   * Send a non-streaming request to OpenAI
   */
  async sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/chat/completions`;
      const openAIRequest = this.convertToOpenAIFormat(
        request,
        context.resolvedModel
      );
      openAIRequest.stream = false;

      this.logger.debug("Sending request to OpenAI", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers: this.buildHeaders(context),
        body: JSON.stringify(openAIRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorBody: OpenAIError | string;
        try {
          errorBody = (await response.json()) as OpenAIError;
        } catch {
          errorBody = await response.text();
        }
        throw this.handleError(response.status, errorBody);
      }

      const data = (await response.json()) as OpenAIResponse;
      const latencyMs = Date.now() - startTime;

      this.recordRequest(true, latencyMs);
      this.logger.debug("OpenAI request successful", { latencyMs });

      return this.convertFromOpenAIFormat(data, context.resolvedModel);
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      this.logger.error("OpenAI request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Send a streaming request to OpenAI
   */
  async sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/chat/completions`;
      const openAIRequest = this.convertToOpenAIFormat(
        request,
        context.resolvedModel
      );
      openAIRequest.stream = true;

      this.logger.debug("Starting streaming request to OpenAI", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers: this.buildHeaders(context),
        body: JSON.stringify(openAIRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorBody: OpenAIError | string;
        try {
          errorBody = (await response.json()) as OpenAIError;
        } catch {
          errorBody = await response.text();
        }
        throw this.handleError(response.status, errorBody);
      }

      if (!response.body) {
        throw new Error("No response body for streaming request");
      }

      // Generate message ID for this stream
      const messageId = `openai-${Date.now()}`;

      // Send initial message_start event (Anthropic SSE format)
      handler.onEvent(
        "message_start",
        JSON.stringify({
          type: "message_start",
          message: {
            id: messageId,
            type: "message",
            role: "assistant",
            content: [],
            model: context.resolvedModel,
            stop_reason: null,
            stop_sequence: null,
            usage: { input_tokens: 0, output_tokens: 0 },
          },
        })
      );

      // Send content_block_start
      handler.onEvent(
        "content_block_start",
        JSON.stringify({
          type: "content_block_start",
          index: 0,
          content_block: { type: "text", text: "" },
        })
      );

      // Process SSE stream from OpenAI
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let outputTokens = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmedLine = line.trim();

          // Skip empty lines
          if (!trimmedLine) continue;

          // Check for SSE data prefix
          if (!trimmedLine.startsWith("data:")) continue;

          const data = trimmedLine.slice(5).trim();

          // Check for stream end
          if (data === "[DONE]") {
            // Send final events
            handler.onEvent(
              "content_block_stop",
              JSON.stringify({
                type: "content_block_stop",
                index: 0,
              })
            );

            handler.onEvent(
              "message_delta",
              JSON.stringify({
                type: "message_delta",
                delta: {
                  stop_reason: "end_turn",
                  stop_sequence: null,
                },
                usage: {
                  output_tokens: outputTokens,
                },
              })
            );

            handler.onEvent(
              "message_stop",
              JSON.stringify({
                type: "message_stop",
              })
            );
            continue;
          }

          try {
            const chunk = JSON.parse(data) as OpenAIStreamChunk;
            const choice = chunk.choices[0];

            if (choice?.delta?.content) {
              // Estimate output tokens (rough approximation)
              outputTokens += Math.ceil(choice.delta.content.length / 4);

              // Convert to Anthropic content_block_delta format
              handler.onEvent(
                "content_block_delta",
                JSON.stringify({
                  type: "content_block_delta",
                  index: 0,
                  delta: {
                    type: "text_delta",
                    text: choice.delta.content,
                  },
                })
              );
            }

            // Handle finish_reason in streaming
            if (choice?.finish_reason) {
              handler.onEvent(
                "content_block_stop",
                JSON.stringify({
                  type: "content_block_stop",
                  index: 0,
                })
              );

              handler.onEvent(
                "message_delta",
                JSON.stringify({
                  type: "message_delta",
                  delta: {
                    stop_reason: this.mapFinishReason(choice.finish_reason),
                    stop_sequence: null,
                  },
                  usage: {
                    output_tokens: outputTokens,
                  },
                })
              );

              handler.onEvent(
                "message_stop",
                JSON.stringify({
                  type: "message_stop",
                })
              );
            }
          } catch {
            // Skip malformed lines
            this.logger.debug("Skipping malformed OpenAI response line", {
              line: trimmedLine,
            });
          }
        }
      }

      const latencyMs = Date.now() - startTime;
      this.recordRequest(true, latencyMs);
      handler.onComplete();

      this.logger.debug("OpenAI streaming request completed", { latencyMs });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      const err = error instanceof Error ? error : new Error("Unknown error");
      this.logger.error("OpenAI streaming request failed", {
        error: err.message,
        latencyMs,
      });

      handler.onError(err);
    }
  }

  /**
   * Health check for OpenAI
   */
  override async healthCheck(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const apiKey = this.getNextApiKey();
      const headers: Record<string, string> = {};
      if (apiKey) {
        headers["Authorization"] = `Bearer ${apiKey}`;
      }

      // OpenAI doesn't have a dedicated health endpoint, so we check the models endpoint
      const response = await fetch(`${this.baseUrl}/models`, {
        method: "GET",
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      this.recordHealthCheck(response.ok);
      return response.ok;
    } catch (error) {
      this.recordHealthCheck(
        false,
        error instanceof Error ? error.message : "Unknown error"
      );
      return false;
    }
  }
}

/**
 * Create an OpenAI provider from config
 */
export function createOpenAIProvider(config: ProviderConfig): OpenAIProvider {
  return new OpenAIProvider(config);
}
